import { graphql, readInlineData } from "react-relay";

import { article as articleData, paywall } from "shared/data";
import { notEmpty } from "shared/lib/utils";

import { getArticleType } from "scmp-app/components/article/article-render/helpers";
import { checkIsPlusArticle } from "scmp-app/components/article/article-render/plus/helpers";
import type { helpersIsArchiveWallArticle$key } from "scmp-app/queries/__generated__/helpersIsArchiveWallArticle.graphql";
import type { helpersIsLiteWallArticle$key } from "scmp-app/queries/__generated__/helpersIsLiteWallArticle.graphql";
import type { helpersIsPlusInlinePaywall$key } from "scmp-app/queries/__generated__/helpersIsPlusInlinePaywall.graphql";
import type { helpersIsSignInWallArticle$key } from "scmp-app/queries/__generated__/helpersIsSignInWallArticle.graphql";
import type { helpersIsVelocityWallArticle$key } from "scmp-app/queries/__generated__/helpersIsVelocityWallArticle.graphql";
export const checkArticleIsInLiteUpgradeWall = (
  article_: helpersIsLiteWallArticle$key,
  isLiteUser: boolean,
  liteArticleTimeFrame: number,
) => {
  const article = readInlineData(
    graphql`
      fragment helpersIsLiteWallArticle on Article @inline {
        createdDate
        publishedDate
      }
    `,
    article_,
  );

  const isLiteArticleDate = Date.now() - 1000 * 60 * 60 * 24 * liteArticleTimeFrame;
  const articleDate = notEmpty(article.publishedDate) ? article.publishedDate : article.createdDate;

  return isLiteUser && !!articleDate && isLiteArticleDate > articleDate;
};

export const checkArticleIsInArchiveWall = (
  article_: helpersIsArchiveWallArticle$key,
  archiveArticleTimeFrame: number,
) => {
  const article = readInlineData(
    graphql`
      fragment helpersIsArchiveWallArticle on Article @inline {
        createdDate
        publishedDate
        paywallTypes {
          entityId
        }
        ...helpersIsVelocityWallArticle
      }
    `,
    article_,
  );
  const isArchivedArticleDate = Date.now() - 1000 * 60 * 60 * 24 * archiveArticleTimeFrame;
  const articleDate = notEmpty(article.publishedDate) ? article.publishedDate : article.createdDate;
  // If either date is empty, it should not in archive wall
  if (!notEmpty(isArchivedArticleDate) || !notEmpty(articleDate)) return false;

  const paywallTypesHasNotArchived = notEmpty(
    article.paywallTypes?.find?.(
      paywallType => paywallType?.entityId === paywall.notArchive.entityId,
    ),
  );

  const isInVelocityWall = checkArticleIsInVelocityWall(article);
  return !paywallTypesHasNotArchived && !isInVelocityWall && isArchivedArticleDate > articleDate;
};

export const checkArticleIsInVelocityWall = (article_: helpersIsVelocityWallArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersIsVelocityWallArticle on Article @inline {
        paywallTypes {
          entityId
        }
      }
    `,
    article_,
  );
  const paywallTypesHasNotVelocity = notEmpty(
    article.paywallTypes?.find?.(
      paywallType => paywallType?.entityId === paywall.notVelocity.entityId,
    ),
  );

  const paywallTypesHasVelocity = notEmpty(
    article.paywallTypes?.find?.(
      paywallType => paywallType?.entityId === paywall.velocity.entityId,
    ),
  );

  return !paywallTypesHasNotVelocity && paywallTypesHasVelocity;
};

export const checkArticleIsInSignInWall = (article_: helpersIsSignInWallArticle$key) => {
  const article = readInlineData(
    graphql`
      fragment helpersIsSignInWallArticle on Article @inline {
        paywallTypes {
          entityId
        }
      }
    `,
    article_,
  );

  const paywallTypesHasSignIn = notEmpty(
    article.paywallTypes?.find?.(paywallType => paywallType?.entityId === paywall.signIn.entityId),
  );

  return paywallTypesHasSignIn;
};

export const checkArticleIsPlusInlinePaywall = (article_: helpersIsPlusInlinePaywall$key) => {
  const article = readPlusInlinePaywallData(article_);

  const isPlusArticle = checkIsPlusArticle(article);
  const articleTypeByEntityId = getArticleType(article);

  // Plus articles server-marked as unlocked (contentLock: false) should render
  // in the inline-paywall flow rather than trigger the full-page Plus redirect
  if (isPlusArticle && article.contentLock === false) return true;

  return (
    isPlusArticle && articleTypeByEntityId === articleData.types.chinaFutureTechBriefing.entityId
  );
};

/**
 * Whether an article on the Plus platform should follow the inline paywall
 * flow (article renders in the Plus popup) instead of the full-page flow
 * (redirect to the /plus/learnmore paywall page). True for Plus inline-paywall
 * articles (e.g. chinaFutureTechBriefing) and for any article the server
 * marked as unlocked (contentLock: false), including platform-featured
 * non-Plus articles opened via display=plus.
 *
 * Consumed server-side (next-router) via the isPlusInlinePaywall query
 * parameter, and client-side as fallback by the Plus paywall hook runner.
 */
export const checkArticleShouldRenderPlusPaywall = (article_: helpersIsPlusInlinePaywall$key) =>
  readPlusInlinePaywallData(article_).contentLock === false ||
  checkArticleIsPlusInlinePaywall(article_);

const readPlusInlinePaywallData = (article_: helpersIsPlusInlinePaywall$key) =>
  readInlineData(
    graphql`
      fragment helpersIsPlusInlinePaywall on Content @inline {
        contentLock
        ...helpersGetArticleTypeArticle
        ...helpersCheckIsPlusArticle
      }
    `,
    article_,
  );
